import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, startsWithin } from './effectiveWindow';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import { teamLabel } from './teamLabel';
import MetricDefinitionNote from './MetricDefinitionNote';

const OPERATORS = [
  { value: '<', label: 'is below' },
  { value: '<=', label: 'is at or below' },
  { value: '=', label: 'equals' },
  { value: '>=', label: 'is at or above' },
  { value: '>', label: 'is above' },
];

const emptyForm = { crm_user_id: '', comparison_operator: '<', threshold_value: '', is_active: true };

// Sales Count's config screen - Team model (a team is named after its head: "ANITA team", see teamLabel.js) (Requirement Document
// v1.0 S3/S4.13). A "team" is one CRM's live reporting chain, not a
// stored list - Finance picks the CRM from a dropdown, not a team name.
// Add/edit is an inline panel on the page, not a popup.
const CrmTeamConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmUsersLoading, setCrmUsersLoading] = useState(true);
  const [crmUsersError, setCrmUsersError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // a blank threshold only complains after the first Save attempt
  const [submitted, setSubmitted] = useState(false);

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no thresholds configured".
    return apiRequest({ url: `warning-dashboard/crm-team-thresholds?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load CRM team thresholds');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load CRM team thresholds');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  // The CRM dropdown's own load - a failure used to leave the list silently empty. Retry reloads only this list.
  const loadCrmUsers = () => {
    setCrmUsersLoading(true);
    setCrmUsersError(null);
    return apiRequest({ url: 'warning-dashboard/crm-users', method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') {
          setCrmUsersError(res?.message || 'Could not load the CRM list');
          return;
        }
        setCrmUsers(res?.data?.rows || []);
      })
      .catch((err) => setCrmUsersError(err?.response?.data?.message || 'Could not load the CRM list'))
      .finally(() => setCrmUsersLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  useEffect(() => {
    loadCrmUsers();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, ...emptyEffective() }); // the month to apply from is read fresh each time the form opens
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      crm_user_id: row.crm_user_id,
      comparison_operator: row.comparison_operator,
      threshold_value: row.threshold_value,
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  // BUG-03: never blank, never negative; and one current threshold per CRM (the server enforces both too)
  const thresholdField = numberField(form.threshold_value, 'Threshold', RULES.nonNegative, submitted, { helper: false });
  // Several versions of one threshold are normal now (a change from a later month, a one-month override), so only a
  // second ACTIVE OPEN-ENDED row starting in the chosen month or later is a conflict - one that starts earlier is simply closed when the new one begins. Edits and one-month overrides are checked by the server, which knows the months.
  const duplicateError =
    form.crm_user_id && !editingId && rows.find((r) => r.is_active && startsWithin(r, form) && String(r.crm_user_id) === String(form.crm_user_id))
      ? `${crmUsers.find((u) => String(u.userID) === String(form.crm_user_id))?.name || 'This CRM'} already has a threshold that starts in the chosen month or later - edit it, or choose a later month`
      : '';
  const keyError = submitted && !form.crm_user_id ? 'Choose the team head' : '';

  const handleSave = () => {
    setSubmitted(true);
    if (!form.crm_user_id || duplicateError || validateNumber(form.threshold_value, 'Threshold', RULES.nonNegative)) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/crm-team-thresholds/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/crm-team-thresholds', method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this threshold');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this threshold'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r)));
    apiRequest({ url: `warning-dashboard/crm-team-thresholds/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
        } else {
          fetchRows({ silent: true }); // a toggle can start a new version from this month
        }
      })
      .catch(() => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
      })
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  const operatorLabel = (op) => OPERATORS.find((o) => o.value === op)?.label || op;

  return (
    <WarningPage
      title="Sales Count - Team Thresholds"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard/sales-count?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
            View Results
          </Button>
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>
              Add Team Threshold
            </Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A team is one CRM's current reporting chain, resolved live - not a
        stored roster. The threshold is a monthly count, prorated for a
        shorter period.
      </Typography>

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {editingId ? 'Edit Team Threshold' : 'Add Team Threshold'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <MetricDefinitionNote metricKey="sales_count" />
            <TextField
              select
              label="Team head"
              value={form.crm_user_id}
              onChange={(e) => setForm({ ...form, crm_user_id: e.target.value })}
              error={!!(keyError || duplicateError || crmUsersError)}
              helperText={crmUsersError ? <RetryInline message={crmUsersError} loading={crmUsersLoading} onRetry={loadCrmUsers} /> : keyError || duplicateError || undefined}
              sx={{ maxWidth: 320 }}
              disabled={crmUsersLoading}
            >
              {crmUsers.map((u) => (
                <MenuItem key={u.userID} value={u.userID}>{u.name}</MenuItem>
              ))}
            </TextField>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography>Warns when sales</Typography>
              <TextField
                select
                size="small"
                value={form.comparison_operator}
                onChange={(e) => setForm({ ...form, comparison_operator: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                {OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                value={form.threshold_value}
                onChange={(e) => setForm({ ...form, threshold_value: e.target.value })}
                sx={{ width: 120 }}
                {...thresholdField.props}
              />
              <Typography>/ month</Typography>
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, no warning is raised for this CRM's team." />

            {thresholdField.error && <Typography color="error.main" variant="body2">{thresholdField.error}</Typography>}
            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading thresholds...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && rows.length === 0 && (
        <Typography color="text.secondary">No team thresholds configured yet.</Typography>
      )}

      {!rowsLoading && !rowsError && rows.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Team</strong></TableCell>
                <TableCell><strong>Warns when sales</strong></TableCell>
                <TableCell><strong>Applies</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{teamLabel(row.crmUserName)}</TableCell>
                  <TableCell>{operatorLabel(row.comparison_operator)} {row.threshold_value} / month</TableCell>
                  <TableCell><AppliesCell row={row} month={month} /></TableCell>
                  <TableCell>
                    <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                  </TableCell>
                  <TableCell>
                    <UpdateButton size="small" action={() => openEdit(row)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default CrmTeamConfig;
